import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '../supabase';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const [error, setError] = useState('');

  useEffect(() => {
    const handleVerification = async () => {
      // Supabase handles email confirmation automatically via the hash fragment.
      // When users click the confirm link, Supabase redirects to the app with
      // #access_token=...&token_type=bearer&type=signup in the URL.
      // The Supabase client picks it up in onAuthStateChange.
      // We just need to wait briefly and check the session.
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.email_confirmed_at) {
          setStatus('success');
          setMessage('Your email has been verified successfully!');
          setTimeout(() => navigate('/'), 2000);
        } else if (session) {
          // Logged in but not verified — may still be processing
          setStatus('success');
          setMessage('Email verified! Redirecting...');
          setTimeout(() => navigate('/'), 2000);
        } else {
          // No session — the Supabase redirect may carry a token in the URL hash.
          // Let Supabase handle it; if there's an error param show it.
          const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
          const errorDescription = hashParams.get('error_description');
          if (errorDescription) {
            setStatus('error');
            setError(errorDescription);
          } else {
            // Likely still loading the session — give it a moment
            setTimeout(async () => {
              const { data: { session: s2 } } = await supabase.auth.getSession();
              if (s2) {
                setStatus('success');
                setMessage('Email verified! Redirecting...');
                setTimeout(() => navigate('/'), 2000);
              } else {
                setStatus('error');
                setError('Invalid or expired verification link. Please request a new verification email.');
              }
            }, 1500);
          }
        }
      } catch (err: unknown) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Failed to verify email.');
      }
    };

    handleVerification();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text flex items-center justify-center px-4 bg-tech-mesh">
      <div className="max-w-md w-full">
        <div className="tech-card rounded-2xl p-8 border-tech-cyan/10 text-center">
          {status === 'verifying' && (
            <>
              <div className="flex justify-center mb-6">
                <Loader2 className="w-12 h-12 text-tech-cyan animate-spin" aria-hidden />
              </div>
              <h2 className="text-2xl font-bold text-tech-text mb-2">Verifying Email</h2>
              <p className="text-tech-text-muted">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-tech-accent" aria-hidden />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-tech-accent">Email Verified!</h2>
              <p className="text-tech-text-muted mb-6">{message}</p>
              <p className="text-tech-text-muted text-sm">Redirecting you now...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-6">
                <XCircle className="w-12 h-12 text-amber-400" aria-hidden />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-amber-400">Verification Failed</h2>
              <p className="text-tech-text-muted mb-6">{error}</p>
              <div className="space-y-4">
                {currentUser && !currentUser.emailVerified && (
                  <Link
                    to="/resend-verification"
                    className="inline-block w-full py-3 rounded-xl font-semibold text-tech-dark hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-tech-cyan/50"
                    style={{ background: 'linear-gradient(135deg, #0066ff 0%, #00d9ff 100%)' }}
                  >
                    Resend Verification Email
                  </Link>
                )}
                <Link
                  to="/login"
                  className="inline-block w-full py-3 tech-card rounded-xl font-medium text-tech-cyan border border-tech-cyan/50 hover:bg-tech-cyan/10 transition-colors"
                >
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
